import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { RadioGroup } from "@/components/ui/radio-group";
import Banner from "@/components/ui/banner";
import IconContainer from "@/components/ui/icon-container";
import { Amendment, Edit, InfoCircle } from "@dreamhub/designsystem/icons";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { TermsVersionSummary } from "@/pages/contracts/components/termsHistory/types";
import UpdateTermsKindCard from "./UpdateTermsKindCard";
import { TermsUpdateKind } from "./types";

interface Props {
  isOpen: boolean;
  /** The scheduled amendment, when the contract carries one. */
  pendingAmendment?: TermsVersionSummary | null;
  onClose: () => void;
  onContinue: (kind: TermsUpdateKind) => void;
}

// Early termination is drawn in Figma with placeholder copy but is out of scope.
// LogoIconContainer's only frame-colour API is `index`, which selects from the
// shared milestone *border* palette. These two are picked for hue, not value:
// entry 0 is a blue (#3366FF) that pairs with the blue-700 tile and entry 3 a
// gold (#EC9D16) that pairs with the gold-900 one. The palette belongs to deal
// milestones, so re-ordering it would retint these cards.
const AMENDMENT_PALETTE_INDEX = 0;
const CORRECTION_PALETTE_INDEX = 3;

const KIND_CARDS = [
  {
    kind: TermsUpdateKind.AMENDMENT,
    icon: <Amendment />,
    iconBgColor: "var(--blue-700)",
    iconPaletteIndex: AMENDMENT_PALETTE_INDEX
  },
  {
    kind: TermsUpdateKind.CORRECTION,
    icon: <Edit />,
    iconBgColor: "var(--gold-900)",
    iconPaletteIndex: CORRECTION_PALETTE_INDEX
  }
] as const;

const UpdateTermsModal: React.FC<Props> = ({
  isOpen,
  pendingAmendment = null,
  onClose,
  onContinue
}) => {
  const { t } = useTranslation();
  const [selectedKind, setSelectedKind] = useState<TermsUpdateKind | null>(
    null
  );

  // Reopening starts from no selection, never a cancelled kind.
  useEffect(() => {
    if (!isOpen) setSelectedKind(null);
  }, [isOpen]);

  // Only the amendment card carries one: a second future-dated version is
  // refused, and the correction card's own consequence — that saving cancels
  // the scheduled amendment — is confirmed at save, where dismissing can still
  // leave the edited set intact.
  // A second future-dated version is refused at save, so the amendment card
  // does not open an editor whose work cannot be kept. The note beside it is
  // what explains the refusal.
  const isKindDisabled = (kind: TermsUpdateKind): boolean =>
    kind === TermsUpdateKind.AMENDMENT && !!pendingAmendment;

  // The version list can resolve while the picker is already open, so a card
  // selected a moment ago can become disabled under the selection. Continue is
  // gated on the same predicate and would refuse, but the card would keep
  // reading as chosen — selected and refused at once.
  //
  // The condition is spelled out rather than calling `isKindDisabled`: that
  // function is rebuilt every render, so depending on it would re-run this
  // effect every render, and omitting it is what exhaustive-deps flags.
  useEffect(() => {
    if (selectedKind === TermsUpdateKind.AMENDMENT && pendingAmendment) {
      setSelectedKind(null);
    }
  }, [selectedKind, pendingAmendment]);

  const kindNotice = (kind: TermsUpdateKind): React.ReactNode => {
    if (kind !== TermsUpdateKind.AMENDMENT) return null;
    if (!pendingAmendment?.effectiveDate) return null;

    return (
      <Banner variant="info" className="p-2">
        <div
          className="flex items-start gap-2"
          data-test="update-terms-pending-notice"
        >
          <IconContainer size="sm" className="text-natural shrink-0">
            <InfoCircle />
          </IconContainer>
          <Text variant="smBody" className="text-natural">
            {t("contracts.updateTerms.kinds.AMENDMENT.pendingNotice", {
              date: formatDateShort(pendingAmendment.effectiveDate)
            })}
          </Text>
        </div>
      </Banner>
    );
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      contentClassname="w-[60rem] max-w-[95vw]"
      data-test="update-terms-modal"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t("contracts.updateTerms.modal.title")}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 px-6 pt-6 pb-10">
          <Text as="div" variant="body" className="pl-1">
            {t("contracts.updateTerms.modal.prompt")}
          </Text>
          <RadioGroup
            className="flex flex-row items-stretch gap-4"
            value={selectedKind ?? ""}
            onValueChange={(value) => {
              const kind = value as TermsUpdateKind;
              if (!isKindDisabled(kind)) setSelectedKind(kind);
            }}
          >
            {KIND_CARDS.map(({ kind, icon, iconBgColor, iconPaletteIndex }) => (
              <UpdateTermsKindCard
                key={kind}
                notice={kindNotice(kind)}
                isDisabled={isKindDisabled(kind)}
                value={kind}
                icon={icon}
                iconBgColor={iconBgColor}
                iconPaletteIndex={iconPaletteIndex}
                isSelected={selectedKind === kind}
                onSelect={() => setSelectedKind(kind)}
                title={t(`contracts.updateTerms.kinds.${kind}.title`)}
                description={t(
                  `contracts.updateTerms.kinds.${kind}.description`
                )}
                footnote={t(`contracts.updateTerms.kinds.${kind}.footnote`)}
                data-test={`update-terms-kind-card-${kind}`}
              />
            ))}
          </RadioGroup>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondaryNoBg"
            onClick={onClose}
            data-test="update-terms-modal-cancel"
          >
            {t("cancel")}
          </Button>
          <Button
            disabled={!selectedKind || isKindDisabled(selectedKind)}
            onClick={() =>
              selectedKind &&
              !isKindDisabled(selectedKind) &&
              onContinue(selectedKind)
            }
            data-test="update-terms-modal-continue"
          >
            {t("contracts.updateTerms.modal.continue")}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

export default UpdateTermsModal;
